import { DragDropModule } from '@angular/cdk/drag-drop';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { InjectionToken, NgModule, Optional } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatRadioModule } from '@angular/material/radio';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink, RouterModule } from '@angular/router';
import { MaskitoDirective } from '@maskito/angular';
import { EffectsModule } from '@ngrx/effects';
import { StoreConfig, StoreModule } from '@ngrx/store';
import { WebcamModule } from 'ngx-webcam';

import {
  ENGINE_FEATURE_KEY,
  ENGINE_STATE_EXTENSION,
  type EngineStateBase,
  pageContextFeature,
} from '@blaze-runtime/engine-data-access';
import { ComplexFiltersModule, VirtualScrollModule } from '@blaze-runtime/engine-ui-feature';
import { CustomTemplateRendererComponent } from '@blaze-runtime/html-template-renderer-feature';
import {
  EngineConfirmDialogs,
  EngineDialogPages,
  EngineFileDownloads,
  EnginePageNavigation,
} from '@blaze-runtime/workflow-feature';
import { ISearchAndFiltersWrapperService } from '@blaze-shared/elements-domain';
import { IntellisenseCoreModule } from '@blaze-shared/intellisense-feature';
import { SCHEMA_VALIDATION_ENABLED } from '@blaze-shared/platform-data-access';
import { FormatValueModule, SharedModule } from '@blaze-shared/shared-feature';
import {
  AutoSlashDateInputDirective,
  CheckboxComponent as BlazeCheckboxComponent,
  ClickableDirective,
  ImgErrorModule,
  InputMaskDirective,
  ItemSelectorComponent,
  LoadingIndicatorComponent,
  MarkdownPipe,
  PaginatorModule,
  ResizedModule,
  ScrollerComponent,
  SecurityTrustModule,
  DialogComponent as SharedDialogComponent,
  TextTruncateModule,
  TinyMceModule,
  TooltipDirective,
} from '@blaze-shared/shared-ui';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { BulkActionsFacade } from './+state';
import { createAiUndoMetaReducer } from './+state/ai/ai-undo.meta-reducer';
import { BulkActionsEffects } from './+state/bulk-actions/bulk-actions.effects';
import { ChartEffects } from './+state/chart/chart.effects';
import { ChartsFacade } from './+state/chart/charts.facade';
import { ElementsFacade } from './+state/elements/elements.facade';
import { EngineEffects } from './+state/engine/engine.effects';
import { createEngineReducer, ENGINE_REDUCER } from './+state/engine/engine.reducer';
import { GenerationEffects } from './+state/generation/generation.effects';
import { NavMenuEffects } from './+state/nav-menu/nav-menu.effects';
import { RowActionPageGenerationEffects } from './+state/row-action-page-generation/row-action-page-generation.effects';
import { RuntimeCoreEffects } from './+state/runtime/runtime-core.effects';
import { RuntimeEffects } from './+state/runtime/runtime.effects';
import { UiEffects } from './+state/runtime/ui.effects';
import { WorkflowNodesEffects } from './+state/runtime/workflow-nodes.effects';
import { WorkflowEffects } from './+state/runtime/workflow.effects';
import { StylesMigrationEffects } from './+state/styles-migration/styles-migration.effects';
import { AiUndoService } from './ai';
import { CanvasComponent } from './canvas/canvas.component';
import { ElementInputProviderService } from './canvas-elements/io-data/inputs/element-input-data-provider.service';
import { INPUT_DATA_STREAM } from './canvas-elements/io-data/inputs/input-data-stream.token';
import { ElementNodeDirective } from './canvas-elements/rendering/element-node.directive';
import { RenderElementHostDirective } from './canvas-elements/rendering/render-element-host.directive';
import { RenderElementDirective } from './canvas-elements/rendering/render-element.directive';
import { SlotMarkerDirective } from './canvas-elements/rendering/slot-marker.directive';
import { SlotNodeDirective } from './canvas-elements/rendering/slot-node.directive';
import { BulkEditComponent } from './components/bulk-edit/bulk-edit.component';
import { CalendarDatesViewComponent } from './components/calendar-dates-view/calendar-dates-view.component';
import { CalendarHeaderComponent } from './components/calendar-header/calendar-header.component';
import { FilterBuilderComponent } from './components/complex-filters/filter-builder/filter-builder.component';
import { FilterBuilderTriggerComponent } from './components/complex-filters/filter-builder-trigger/filter-builder-trigger.component';
import { SavedFiltersComponent } from './components/complex-filters/saved-filters/saved-filters.component';
import { DialogConfirmComponent } from './components/dialog-confirm/dialog-confirm.component';
import { ElementFilterHeaderComponent } from './components/element-filter-header/element-filter-header.component';
import { ElementHeaderComponent } from './components/element-header/element-header.component';
import { ElementLabelComponent } from './components/element-label/element-label.component';
import { FileColumnValueComponent } from './components/file-column-value/file-column-value.component';
import { ImageColumnValueComponent } from './components/image-column-value/image-column-value.component';
import { InlineProgressBarComponent } from './components/inline-progress-bar/inline-progress-bar.component';
import { MobilePaginationComponent } from './components/mobile-pagination/mobile-pagination.component';
import { SelectableTileComponent } from './components/selectable-tile/selectable-tile.component';
import { SignatureColumnValueComponent } from './components/signature-column-value/signature-column-value.component';
import { TagsColumnValueComponent } from './components/tags-column-value/tags-column-value.component';
import { AvoidCloseButtonDirective } from './directives/avoid-close-button.directive';
import { ElementSelectionHelperDirective } from './directives/element-selection-helper.directive';
import { ReferenceDragHitboxDirective } from './directives/reference-drag-hitbox.directive';
import {
  AccountsDropdownComponent,
  BarcodeScannerComponent,
  BarcodeScannerOverlayComponent,
  BreakLineComponent,
  ButtonComponent,
  CalendarComponent,
  CalendarDayViewComponent,
  CalendarEventComponent,
  CalendarMonthViewComponent,
  CalendarsFilterComponent,
  CalendarWeekViewComponent,
  ChartComponent,
  ChatComponent,
  CheckboxComponent,
  CheckboxInputComponent,
  CollapsibleContainerComponent,
  ConnectionSelectorComponent,
  ContainerComponent,
  CountdownTimerComponent,
  DataFilterComponent,
  DatetimeInputComponent,
  DialogComponent,
  DrawerComponent,
  DropdownComponent,
  EventDragMoveDirective,
  EventDragResizeDirective,
  EventHeightPipe,
  EventPositionTopPipe,
  FormContainerComponent,
  FormGroupComponent,
  FormListComponent,
  GanttChartComponent,
  GanttChartResizeColumnComponent,
  HtmlCardComponent,
  IFrameComponent,
  ImageComponent,
  InlineDateComponent,
  InlineDateTimeComponent,
  InlineTimeComponent,
  IsActionDisabledPipe,
  IsActionPendingPipe,
  IsAllSelectedPipe,
  IsColumnPercentagePipe,
  KanbanBoardComponent,
  ListComponent,
  ListElementRatingComponent,
  MapComponent,
  NavBottombarComponent,
  NavContainerComponent,
  NavMenuComponent,
  NavSidebarComponent,
  NotificationButtonComponent,
  OptionLabelPipe,
  OverlayComponent,
  PageBreakComponent,
  PaymentButtonComponent,
  PdfViewerComponent,
  QrCodeComponent,
  RadioComponent,
  ResizeColumnComponent,
  ResizeDrawerComponent,
  RootComponent,
  RowIndexPipe,
  SchedulerComponent,
  SignatureComponent,
  SliderComponent,
  StylesForTagPipe,
  TableBaseEditorComponent,
  TableBooleanEditorComponent,
  TableComponent,
  TableCurrencyEditorComponent,
  TableDateTimeEditorComponent,
  TableDurationEditorComponent,
  TableFileMenuComponent,
  TableGeoPointEditorComponent,
  TableHeaderComponent,
  TableImageMenuComponent,
  TableLinkMenuComponent,
  TableNumberEditorComponent,
  TableParagraphEditorComponent,
  TablePercentEditorComponent,
  TablePhoneEditorComponent,
  TableRowComponent,
  TableSecureTextEditorComponent,
  TableTagMenuComponent,
  TableTimeZoneMenuComponent,
  TabsComponent,
  TagsComponent,
  TextAreaComponent,
  TextFieldComponent,
  TextInputComponent,
  TimeTrackDirective,
  ToggleButtonComponent,
  UploadFileComponent,
  VideoPlayerComponent,
  WizardComponent,
  WrapperComponent,
} from './elements';
import { BreadcrumbsComponent } from './elements/breadcrumbs/breadcrumbs.component';
import { HighlightSearchResultPipe } from './elements/chat/conversations-search-results/highlight-search-result.directive';
import { DropdownInfiniteScrollDirective } from './elements/dropdown/dropdown-infinite-scroll.directive';
import { TableBodyComponent } from './elements/table/table-body/table-body.component';
import { createEngineMetaReducer } from './engine-error-logging-metareducer';
import { NodeRendererComponent } from './node-renderer/node-renderer.component';
import { ApplicationDialogService } from './services/dialog/application-dialog.service';
import { DialogOverlayService } from './services/dialog/dialog-overlay.service';
import { FileService } from './services/file.service';
import { PendingBulkActionService } from './services/pending-bulk-actions.service';
import { ProjectNavigateService } from './services/project-navigate.service';
import { SearchAndFiltersWrapperService } from './services/search-and-filters-wrapper.service';
import { StylesCleanup } from './services/styles-cleanup.service';
import { TableInlineEditorUtilsService } from './services/table-inline-editor-utils.service';

const STANDALONE_COMPONENTS = [
  ScrollerComponent,
  // canvas elements components
  BarcodeScannerComponent,
  BarcodeScannerOverlayComponent,
  ConnectionSelectorComponent,
  CountdownTimerComponent,
  DataFilterComponent,
  DatetimeInputComponent,
  DialogComponent,
  DrawerComponent,
  FilterBuilderComponent,
  FilterBuilderTriggerComponent,
  HtmlCardComponent,
  IFrameComponent,
  ImageComponent,
  MapComponent,
  OverlayComponent,
  PageBreakComponent,
  PdfViewerComponent,
  ResizeDrawerComponent,
  SavedFiltersComponent,
  SliderComponent,
  StylesForTagPipe,
  TagsColumnValueComponent,
  UploadFileComponent,
  ToggleButtonComponent,

  ElementFilterHeaderComponent,
  ElementHeaderComponent,
  ElementLabelComponent,
  LoadingIndicatorComponent,

  SlotNodeDirective,
  AvoidCloseButtonDirective,
  ChatComponent,
  HighlightSearchResultPipe,
  DropdownInfiniteScrollDirective,
  MarkdownPipe,

  SelectableTileComponent,
  TableBodyComponent,
  AutoSlashDateInputDirective,
  ResizeColumnComponent,
  CustomTemplateRendererComponent,

  IsActionDisabledPipe,
  IsActionPendingPipe,
  IsAllSelectedPipe,

  InlineDateComponent,
  InlineTimeComponent,
  InlineDateTimeComponent,
  InlineProgressBarComponent,
  IsColumnPercentagePipe,
];

export const ENGINE_STORE_CONFIG_TOKEN = new InjectionToken<StoreConfig<EngineStateBase>>(
  'Engine Store Config',
);

const getEngineStoreConfig = (
  schemaValidationEnabled: boolean,
  undoService: AiUndoService,
): StoreConfig<EngineStateBase> => {
  return {
    metaReducers: [
      createEngineMetaReducer(schemaValidationEnabled),
      createAiUndoMetaReducer(undoService),
    ],
  };
};

@NgModule({
  imports: [
    RouterModule,
    RouterLink,
    CommonModule,
    StoreModule.forFeature(ENGINE_FEATURE_KEY, ENGINE_REDUCER, ENGINE_STORE_CONFIG_TOKEN),
    StoreModule.forFeature(pageContextFeature),
    EffectsModule.forFeature([
      EngineEffects,
      GenerationEffects,
      NavMenuEffects,
      RuntimeCoreEffects,
      RuntimeEffects,
      UiEffects,
      WorkflowEffects,
      WorkflowNodesEffects,
      ChartEffects,
      RowActionPageGenerationEffects,
      BulkActionsEffects,
      StylesMigrationEffects,
    ]),
    TinyMceModule,
    WebcamModule,
    DragDropModule,
    MatChipsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatMenuModule,
    MatPaginatorModule,
    MatRadioModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    TextFieldModule,
    IconsModule,
    FormatValueModule,
    PaginatorModule,
    ResizedModule,
    TextTruncateModule,
    ImgErrorModule,
    ItemSelectorComponent,
    ReactiveFormsModule,
    IntellisenseCoreModule,
    SecurityTrustModule,
    SharedModule,
    ComplexFiltersModule,
    VirtualScrollModule,
    ...STANDALONE_COMPONENTS,
    MaskitoDirective,
    InputMaskDirective,
    ClickableDirective,
    TooltipDirective,
    NgOptimizedImage,
    SharedDialogComponent,
    BlazeCheckboxComponent,
  ],
  declarations: [
    NodeRendererComponent,
    CanvasComponent,

    // canvas elements components
    AccountsDropdownComponent,
    BreakLineComponent,
    ButtonComponent,
    BreadcrumbsComponent,
    CalendarComponent,
    CalendarDatesViewComponent,
    CalendarDayViewComponent,
    CalendarEventComponent,
    CalendarHeaderComponent,
    CalendarMonthViewComponent,
    CalendarsFilterComponent,
    CalendarWeekViewComponent,
    ChartComponent,
    CheckboxComponent,
    CheckboxInputComponent,
    CollapsibleContainerComponent,
    ContainerComponent,
    DropdownComponent,
    FormListComponent,
    FormContainerComponent,
    FormGroupComponent,
    GanttChartComponent,
    GanttChartResizeColumnComponent,
    KanbanBoardComponent,
    ListComponent,
    NavBottombarComponent,
    NavContainerComponent,
    NavMenuComponent,
    NavSidebarComponent,
    NotificationButtonComponent,
    PaymentButtonComponent,
    QrCodeComponent,
    RadioComponent,
    RootComponent,
    SchedulerComponent,
    SignatureComponent,
    TableComponent,
    TableRowComponent,
    TableHeaderComponent,
    TableBaseEditorComponent,
    TableCurrencyEditorComponent,
    TablePhoneEditorComponent,
    TableDurationEditorComponent,
    TableGeoPointEditorComponent,
    TableNumberEditorComponent,
    TablePercentEditorComponent,
    TableSecureTextEditorComponent,
    TableFileMenuComponent,
    TableTagMenuComponent,
    TableLinkMenuComponent,
    TableDateTimeEditorComponent,
    TableTimeZoneMenuComponent,
    TableParagraphEditorComponent,
    TableImageMenuComponent,
    TableBooleanEditorComponent,
    TabsComponent,
    TagsComponent,
    TextAreaComponent,
    TextFieldComponent,
    TextInputComponent,
    VideoPlayerComponent,
    WrapperComponent,
    WizardComponent,

    BulkEditComponent,
    DialogConfirmComponent,
    FileColumnValueComponent,
    ImageColumnValueComponent,
    ListElementRatingComponent,
    SignatureColumnValueComponent,

    ElementNodeDirective,
    ElementSelectionHelperDirective,
    ReferenceDragHitboxDirective,
    RenderElementDirective,
    RenderElementHostDirective,
    SlotMarkerDirective,
    TimeTrackDirective,

    OptionLabelPipe,
    RowIndexPipe,
    EventHeightPipe,
    EventPositionTopPipe,
    EventDragMoveDirective,
    EventDragResizeDirective,
    MobilePaginationComponent,
  ],
  exports: [
    CanvasComponent,

    BreakLineComponent,
    ButtonComponent,
    ContainerComponent,
    DropdownComponent,
    RootComponent,
    TextInputComponent,
    TextAreaComponent,
    DrawerComponent,
    OptionLabelPipe,
  ],
  providers: [
    ElementsFacade,
    StylesCleanup,
    TableInlineEditorUtilsService,
    PendingBulkActionService,
    ChartsFacade,
    BulkActionsFacade,
    {
      provide: ENGINE_REDUCER,
      deps: [[new Optional(), ENGINE_STATE_EXTENSION]],
      useFactory: createEngineReducer,
    },
    {
      provide: ENGINE_STORE_CONFIG_TOKEN,
      deps: [SCHEMA_VALIDATION_ENABLED, AiUndoService],
      useFactory: getEngineStoreConfig,
    },
    { provide: EngineConfirmDialogs, useExisting: ApplicationDialogService },
    { provide: EngineDialogPages, useExisting: DialogOverlayService },
    { provide: EngineFileDownloads, useExisting: FileService },
    { provide: EnginePageNavigation, useExisting: ProjectNavigateService },
    { provide: ISearchAndFiltersWrapperService, useExisting: SearchAndFiltersWrapperService },
    { provide: INPUT_DATA_STREAM, useExisting: ElementInputProviderService },
  ],
})
export class EngineModule {}
